import type {
  DetailNotFoundErrorResponse,
  InternalErrorResponse,
  UpstreamAuthenticationFailedErrorResponse,
  UpstreamBadGatewayErrorResponse,
  UpstreamNotFoundErrorResponse,
  UpstreamUnavailableErrorResponse,
  WrongInvestmentTypeErrorResponse,
} from '#/api/generated/fINRAMarketDataAPI.schemas';
import { getRuntimeConfig } from '#/lib/runtime-config';
import type { ProductTypeSlug } from '#/types';

type ApiErrorBody = Record<string, unknown> & {
  status?: number;
  detail?: unknown;
  code?: unknown;
  service?: unknown;
};

interface ApiErrorDisplay {
  title: string;
  message: string;
}

function isErrorBody(value: unknown): value is ApiErrorBody {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function normalizeErrorBody(body: unknown, status: number): ApiErrorBody {
  if (body && typeof body === 'object' && !Array.isArray(body)) {
    return { ...(body as ApiErrorBody), status };
  }
  if (typeof body === 'string' && body.length > 0) {
    return { status, detail: body };
  }
  return { status, detail: `API error ${status}` };
}
/**
 * Narrows a generated response envelope to its 200 member so call sites reach
 * `.data` without an `as` assertion. `apiFetch` throws on any non-2xx and 200
 * is every endpoint's only success status, so the non-200 branches at call
 * sites satisfy the union rather than handle a reachable error.
 */
export function isSuccessResponse<TResponse extends { status: number }>(
  response: TResponse | null | undefined,
): response is Extract<TResponse, { status: 200 }> {
  return response?.status === 200;
}

function getApiErrorCode(error: unknown): string | undefined {
  return isErrorBody(error) && typeof error.code === 'string'
    ? error.code
    : undefined;
}

export function getApiErrorStatus(error: unknown): number | undefined {
  return isErrorBody(error) && typeof error.status === 'number'
    ? error.status
    : undefined;
}

function hasErrorCode(error: unknown, code: string): boolean {
  return getApiErrorCode(error) === code;
}

function hasServiceName(error: unknown): boolean {
  return isErrorBody(error) && typeof error.service === 'string';
}

export function isDetailNotFoundError(
  error: unknown,
): error is DetailNotFoundErrorResponse {
  return (
    isErrorBody(error) &&
    error.code === 'not_found' &&
    typeof error.symbol === 'string'
  );
}

export function isWrongInvestmentTypeError(
  error: unknown,
): error is WrongInvestmentTypeErrorResponse {
  return (
    isErrorBody(error) &&
    error.code === 'wrong_investment_type' &&
    typeof error.symbol === 'string' &&
    typeof error.requested_investment_type === 'string'
  );
}

export function isUpstreamNotFoundError(
  error: unknown,
): error is UpstreamNotFoundErrorResponse {
  return hasErrorCode(error, 'upstream_not_found') && hasServiceName(error);
}

export function isUpstreamAuthenticationFailedError(
  error: unknown,
): error is UpstreamAuthenticationFailedErrorResponse {
  return (
    hasErrorCode(error, 'upstream_authentication_failed') &&
    hasServiceName(error)
  );
}

export function isUpstreamBadGatewayError(
  error: unknown,
): error is UpstreamBadGatewayErrorResponse {
  return hasErrorCode(error, 'upstream_bad_gateway') && hasServiceName(error);
}

export function isUpstreamUnavailableError(
  error: unknown,
): error is UpstreamUnavailableErrorResponse {
  return hasErrorCode(error, 'upstream_unavailable') && hasServiceName(error);
}

export function isInternalServiceError(
  error: unknown,
): error is InternalErrorResponse {
  return hasErrorCode(error, 'internal_error') && hasServiceName(error);
}

function isServiceFailureError(
  error: unknown,
): error is
  | UpstreamAuthenticationFailedErrorResponse
  | UpstreamBadGatewayErrorResponse
  | UpstreamUnavailableErrorResponse
  | InternalErrorResponse {
  return (
    isUpstreamAuthenticationFailedError(error) ||
    isUpstreamBadGatewayError(error) ||
    isUpstreamUnavailableError(error) ||
    isInternalServiceError(error)
  );
}

export function isNotFoundError(error: unknown): boolean {
  return (
    isDetailNotFoundError(error) ||
    isWrongInvestmentTypeError(error) ||
    isUpstreamNotFoundError(error) ||
    getApiErrorStatus(error) === 404
  );
}

export function isRetryableApiError(error: unknown): boolean {
  const status = getApiErrorStatus(error);

  if (status === 404 || status === 422) {
    return false;
  }

  return !isNotFoundError(error);
}

export function shouldUseFallbackContent(error: unknown): boolean {
  const status = getApiErrorStatus(error);

  if (status !== undefined) {
    return status >= 500;
  }

  return error instanceof Error;
}

export function getApiErrorMessage(
  error: unknown,
  fallback = 'Request failed.',
): string {
  if (
    error instanceof Error &&
    typeof error.message === 'string' &&
    error.message.length > 0
  ) {
    return error.message;
  }

  if (
    isErrorBody(error) &&
    typeof error.detail === 'string' &&
    error.detail.length > 0
  ) {
    return error.detail;
  }

  return fallback;
}

export function getApiErrorDisplay(error: unknown): ApiErrorDisplay {
  if (isServiceFailureError(error)) {
    return {
      title: 'Data temporarily unavailable',
      message: `${error.service} data is temporarily unavailable right now.`,
    };
  }

  return {
    title: 'Something went wrong',
    message: getApiErrorMessage(error),
  };
}

async function parseErrorBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (text.length === 0) {
    return {};
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return { detail: text };
  }
}

export function toApiError(status: number, body: unknown): ApiErrorBody {
  return normalizeErrorBody(body, status);
}

// Orval custom mutator - prepends apiUrl from runtime config so the Shadow DOM embed
// resolves /api/v1/... against the FastAPI host, not the Drupal host.
// Returns { data, status, headers } to match Orval's generated response types.
export async function apiFetch<T>(
  url: string,
  options?: RequestInit,
): Promise<T> {
  const { apiUrl } = getRuntimeConfig();
  const res = await fetch(`${apiUrl}${url}`, options);
  if (!res.ok) {
    throw toApiError(res.status, await parseErrorBody(res));
  }

  return {
    data: await res.json(),
    status: res.status,
    headers: res.headers,
  } as T;
}

// /$category/$id beforeLoad uses this to resolve the API prefix from the route param
const TYPE_TO_PREFIX: Record<ProductTypeSlug, string> = {
  equities: '/api/v1/equity',
  'mutual-funds': '/api/v1/mutual-funds',
  etfs: '/api/v1/etfs',
};

export function getApiPrefix(type: ProductTypeSlug): string {
  return TYPE_TO_PREFIX[type];
}

export const IMPLEMENTED_TYPES: ProductTypeSlug[] = [
  'equities',
  'mutual-funds',
  'etfs',
];
